import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Building2, Users, BarChart4, PlusCircle, Trash2, LogIn, Edit, Loader2, CheckCircle2, XCircle, Settings2, CreditCard, Eye, EyeOff } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  serverTimestamp,
  Timestamp,
  writeBatch,
  deleteField
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/components/AuthProvider';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/use-toast';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { DeleteTenantDialog } from '@/components/dialogs/DeleteTenantDialog';
import { deleteTenant } from '@/utils/deleteTenant';
import { Dialog as ConfirmDialog, DialogContent as ConfirmDialogContent, DialogHeader as ConfirmDialogHeader, DialogTitle as ConfirmDialogTitle, DialogFooter as ConfirmDialogFooter } from '@/components/ui/dialog';
import { PaymentGatewaysForm } from '@/components/admin/PaymentGatewaysForm';
import { useImpersonation } from '@/hooks/useImpersonation';

interface CompanyData {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  status: 'active' | 'inactive';
  createdAt: Timestamp;
  updatedAt: Timestamp;
  ownerId: string;
  subscriptionPlan: 'basic' | 'standard' | 'premium';
  features: {
    recurringInvoices: boolean;
    virtualTerminal: boolean;
    userManagement: boolean;
    donations: boolean;
  };
  userLimit: number;
  notes?: string;
  wikiPayitSettings?: {
    username?: string;
    tokenizationKey?: string;
    securityKey?: string;
  };
  emailSettings?: {
    sendgridApiKey?: string;
    fromEmail?: string;
  };
  enableRecurringInvoices?: boolean;
  enableInstallmentInvoices?: boolean;
}

interface UserData {
  id: string;
  email: string;
  displayName: string;
  role: string;
  status: 'active' | 'inactive' | 'pending';
  createdAt: Timestamp;
  lastLoginAt?: Timestamp;
}

interface ActivityItem {
  id: string;
  action: string;
  performedBy: string;
  performerName?: string;
  timestamp: Timestamp;
  details?: string;
}

const TenantDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditingCompany, setIsEditingCompany] = useState(false);
  const [isDeactivateDialogOpen, setIsDeactivateDialogOpen] = useState(false);
  const [companyForm, setCompanyForm] = useState<Partial<CompanyData>>({});
  const [isAuthorized, setIsAuthorized] = useState(false);
  
  // State for toggling visibility of sensitive fields
  const [showTokenizationKey, setShowTokenizationKey] = useState(false);
  const [showSecurityKey, setShowSecurityKey] = useState(false);
  const [showSendgridKey, setShowSendgridKey] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleteTransactionsDialogOpen, setIsDeleteTransactionsDialogOpen] = useState(false);
  const [isDeletingTransactions, setIsDeletingTransactions] = useState(false);

  // Impersonation
  const { startImpersonation, isImpersonating: isImpersonationActive } = useImpersonation();
  const [isStartingImpersonation, setIsStartingImpersonation] = useState(false);

  // Define the query function outside of useQuery
  const fetchTenantDetails = async () => {
    if (!id || !user || user.role !== 'super_admin') {
      throw new Error('Invalid request - missing ID or unauthorized');
    }
    
    try {
      // Attempt to fetch the tenant directly
      const docRef = doc(db, 'companies', id);
      const snapshot = await getDoc(docRef);
      
      if (!snapshot.exists()) {
        throw new Error('Tenant not found');
      }
      
      const data = snapshot.data() as CompanyData;
      return { ...data, id: snapshot.id };
    } catch (error) {
      throw new Error(`Failed to fetch tenant: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Always call useQuery but control execution with 'enabled'
  const { data: company, isLoading, error, isError } = useQuery({
    queryKey: ['tenant', id],
    queryFn: fetchTenantDetails,
    enabled: !!id && !!user && user.role === 'super_admin', // Only run when conditions are met
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1 // Only retry once to avoid excessive failed requests
  });

  // Fetch company users - always define the query, but control with enabled
  const { data: users, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['tenant-users', id],
    queryFn: async () => {
      if (!id) throw new Error('Tenant ID is required');
      
      const usersQuery = query(
        collection(db, 'users'),
        where('companyId', '==', id)
      );
      
      const snapshot = await getDocs(usersQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as UserData[];
    },
    enabled: !!id && !!user && user.role === 'super_admin'
  });

  // Fetch company activity - always define the query, but control with enabled
  const { data: activity, isLoading: isLoadingActivity } = useQuery({
    queryKey: ['tenant-activity', id],
    queryFn: async () => {
      if (!id) throw new Error('Tenant ID is required');
      
      // This is a placeholder - in a real app, you would have a collection for activity logs
      // For now, we'll return some mock data
      return [
        {
          id: '1',
          action: 'Tenant Created',
          performedBy: user?.uid || '',
          performerName: 'System Administrator',
          timestamp: Timestamp.fromDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)),
          details: 'Created tenant account with initial admin user'
        },
        {
          id: '2',
          action: 'User Added',
          performedBy: company?.ownerId || '',
          performerName: 'Company Admin',
          timestamp: Timestamp.fromDate(new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)),
          details: 'Added user john.doe@example.com'
        },
        {
          id: '3',
          action: 'Plan Changed',
          performedBy: user?.uid || '',
          performerName: 'System Administrator',
          timestamp: Timestamp.fromDate(new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)),
          details: 'Changed subscription plan from Basic to Standard'
        }
      ] as ActivityItem[];
    },
    enabled: !!id && !!user && user.role === 'super_admin'
  });

  // Update company mutation - define upfront
  const updateCompanyMutation = useMutation({
    mutationFn: async (data: Partial<CompanyData>) => {
      if (!id) throw new Error('No tenant ID provided');
      
      const docRef = doc(db, 'companies', id);
      await updateDoc(docRef, data);
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['tenant', id] });
      
      // Reset forms and editing states
      setIsEditingCompany(false);
      setCompanyForm({});
      
      // Show success toast
      toast({
        title: "Success",
        description: "Company settings updated successfully.",
      });
    },
    onError: (error) => {
      console.error('Error updating company:', error);
      toast({
        title: "Error",
        description: "Failed to update company settings. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Toggle tenant status mutation - define upfront
  const toggleStatusMutation = useMutation({
    mutationFn: async (newStatus: 'active' | 'inactive') => {
      if (!id) throw new Error('Tenant ID is required');

      const companyRef = doc(db, 'companies', id);
      const companySnap = await getDoc(companyRef);
      const companyData = companySnap.data();

      if (newStatus === 'inactive') {
        // DEACTIVATING: Save current gateway state and disable all gateways
        const previousGatewayState = {
          paymentGateways: companyData?.paymentGateways || {},
          wikiPayitSettings: companyData?.wikiPayitSettings || {},
          savedAt: serverTimestamp()
        };

        // Disable all gateways
        const disabledGateways = {
          defaults: {},
          providers: {
            nmi: { enabled: false, mode: 'test', credentials: {} },
            stripe: { enabled: false, mode: 'test', credentials: {} },
            payarc: { enabled: false, mode: 'test', credentials: {} }
          }
        };

        await updateDoc(companyRef, {
          status: 'inactive',
          previousGatewayState,
          paymentGateways: disabledGateways,
          wikiPayitSettings: {},
          updatedAt: serverTimestamp()
        });
      } else {
        // REACTIVATING: Restore previous gateway state
        const previousState = companyData?.previousGatewayState;

        const updateData: Record<string, any> = {
          status: 'active',
          updatedAt: serverTimestamp()
        };

        if (previousState) {
          if (previousState.paymentGateways) {
            updateData.paymentGateways = previousState.paymentGateways;
          }
          if (previousState.wikiPayitSettings) {
            updateData.wikiPayitSettings = previousState.wikiPayitSettings;
          }
          updateData.previousGatewayState = deleteField();
        }

        await updateDoc(companyRef, updateData);
      }

      return newStatus;
    },
    onSuccess: (newStatus) => {
      queryClient.invalidateQueries({ queryKey: ['tenant', id] });
      toast({
        title: `Tenant ${newStatus === 'active' ? 'activated' : 'deactivated'}`,
        description: `The tenant has been ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`,
      });
      setIsDeactivateDialogOpen(false);
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to update tenant status: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: 'destructive'
      });
    }
  });

  // Check for super_admin role using useEffect
  useEffect(() => {
    // Redirect if not super_admin
    if (user) {
      if (user.role === 'super_admin') {
        setIsAuthorized(true);
      } else {
        navigate('/app');
      }
    }
  }, [user, navigate]);
  
  // Initialize form when company data changes
  useEffect(() => {
    if (company) {
      setCompanyForm({
        name: company.name,
        email: company.email,
        phone: company.phone,
        notes: company.notes || '',
        wikiPayitSettings: {
          username: company.wikiPayitSettings?.username || '',
          tokenizationKey: company.wikiPayitSettings?.tokenizationKey || '',
          securityKey: company.wikiPayitSettings?.securityKey || ''
        },
        emailSettings: {
          sendgridApiKey: company.emailSettings?.sendgridApiKey || '',
          fromEmail: company.emailSettings?.fromEmail || ''
        }
      });

      // Update page title with tenant name
      document.title = `Tenants - ${company.name}`;

      // Update header title in the banner
      const headerElement = document.querySelector('header h2');
      if (headerElement) {
        headerElement.textContent = `Tenants - ${company.name}`;
      }
    }
  }, [company]);

  // If still loading user, show loading state
  if (!user) {
    return <div className="flex items-center justify-center h-64">
      <Skeleton className="h-8 w-8 rounded-full" />
    </div>;
  }

  // If not super_admin, don't render the component
  if (!isAuthorized) {
    return null;
  }

  // Add error handling UI
  if (isError || error) {
    return (
      <div className="container mx-auto py-8 space-y-4">
        <div className="flex items-center mb-6">
          <Button variant="ghost" onClick={() => navigate('/app/tenants')} className="mr-2">
            <ArrowLeft className="h-4 w-4 mr-1" />
            <span>Back to Tenants</span>
          </Button>
        </div>
        
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">Error Loading Tenant</CardTitle>
            <CardDescription>
              We encountered an error while trying to load this tenant.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              {error instanceof Error 
                ? error.message 
                : "The tenant could not be found or may have been deleted."}
            </p>
            <p className="text-sm text-muted-foreground mb-2">Possible reasons:</p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1 ml-4">
              <li>The tenant ID in the URL is incorrect</li>
              <li>The tenant has been deleted</li>
              <li>There was an error connecting to the database</li>
            </ul>
          </CardContent>
          <CardFooter>
            <Button onClick={() => navigate('/app/tenants')}>
              Return to Tenant Dashboard
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // If tenant data is loading, show loading skeleton
  if (isLoading) {
    return (
      <div className="container mx-auto py-8 space-y-8">
        <div className="flex items-center mb-6">
          <Button variant="ghost" onClick={() => navigate('/app/tenants')} className="mr-2">
            <ArrowLeft className="h-4 w-4 mr-1" />
            <span>Back to Tenants</span>
          </Button>
          <div>
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-40 mt-2" />
          </div>
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-48" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Handle impersonation
  const handleImpersonate = async () => {
    if (!id) return;
    if (isImpersonationActive || isStartingImpersonation) return;

    setIsStartingImpersonation(true);
    try {
      const result = await startImpersonation(id);
      if (!result.success) {
        toast({
          title: 'Impersonation failed',
          description: result.error || 'Unable to impersonate this tenant',
          variant: 'destructive'
        });
        setIsStartingImpersonation(false);
      }
      // On success, the hook redirects to /app
    } catch (error) {
      console.error('[TenantDetail] Impersonation error:', error);
      toast({
        title: 'Impersonation failed',
        description: 'An unexpected error occurred',
        variant: 'destructive'
      });
      setIsStartingImpersonation(false);
    }
  };

  // Handle company form update
  const handleUpdateCompany = () => {
    if (!companyForm.name || !companyForm.email) {
      toast({
        title: 'Validation Error',
        description: 'Company name and email are required',
        variant: 'destructive'
      });
      return;
    }
    
    updateCompanyMutation.mutate(companyForm);
  };

  // Reset sensitive fields visibility when canceling edit
  const handleCancelEdit = () => {
    setIsEditingCompany(false);
    setShowTokenizationKey(false);
    setShowSecurityKey(false);
    setShowSendgridKey(false);
  };

  // Handle toggling tenant status
  const handleToggleStatus = () => {
    if (!company) return;
    
    const newStatus = company.status === 'active' ? 'inactive' : 'active';
    toggleStatusMutation.mutate(newStatus);
  };

  // Format date for display
  const formatDate = (timestamp?: Timestamp | Date | any) => {
    if (!timestamp) return 'N/A';

    try {
      let date: Date;

      // Handle Firestore Timestamp
      if (timestamp?.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      }
      // Handle Date object
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      // Handle timestamp with seconds property (Firestore Timestamp-like object)
      else if (timestamp?.seconds) {
        date = new Date(timestamp.seconds * 1000);
      }
      // Handle numeric timestamp (milliseconds)
      else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      }
      // Handle string date
      else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      }
      else {
        return 'N/A';
      }

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'N/A';
    }
  };

  // Get initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Handle tenant deletion
  const handleDeleteTenant = async (password: string) => {
    if (!id || !company?.name) return;
    
    try {
      await deleteTenant(id, password);
      
      toast({
        title: 'Tenant deleted',
        description: `${company.name} has been permanently deleted.`,
      });
      
      // Navigate back to tenants list
      navigate('/app/tenants');
    } catch (error) {
      console.error('Error deleting tenant:', error);
      setDeleteError(error instanceof Error ? error.message : 'Failed to delete tenant');
      
      toast({
        title: 'Error deleting tenant',
        description: error instanceof Error ? error.message : 'An unknown error occurred',
        variant: 'destructive'
      });
      
      // Keep the dialog open on error
      throw error;
    }
  };

  // Handler to delete only transactions, invoices, donations, and payment methods for this company
  const handleDeleteTransactionsOnly = async () => {
    if (!id) return;
    setIsDeletingTransactions(true);
    try {
      // Helper to batch delete documents from a collection for this company
      const batchDelete = async (collectionName: string) => {
        const q = query(collection(db, collectionName), where('companyId', '==', id));
        const snapshot = await getDocs(q);
        const batchSize = 500;
        let docs = snapshot.docs;
        while (docs.length > 0) {
          const batch = writeBatch(db);
          for (let i = 0; i < Math.min(batchSize, docs.length); i++) {
            batch.delete(docs[i].ref);
          }
          await batch.commit();
          docs = docs.slice(batchSize);
        }
      };
      await batchDelete('transactions');
      await batchDelete('invoices');
      await batchDelete('donations');
      await batchDelete('paymentMethods');
      await batchDelete('products');
      await batchDelete('customers');
      toast({
        title: 'Data Deleted Successfully',
        description: 'All transactions, invoices, donations, payment methods, products, and customers for this tenant have been permanently deleted.',
      });
      setIsDeleteTransactionsDialogOpen(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete transactions',
        variant: 'destructive',
      });
    } finally {
      setIsDeletingTransactions(false);
    }
  };

  return (
    <div className="container mx-auto py-8 space-y-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Button variant="ghost" onClick={() => navigate('/app/tenants')} className="mr-2">
            <ArrowLeft className="h-4 w-4 mr-1" />
            <span>Back to Tenants</span>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {company?.name || 'Tenant Details'}
            </h1>
            <p className="text-muted-foreground">
              {company?.email || 'Loading tenant information...'}
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          {!isEditingCompany && (
            <Button variant="outline" onClick={() => {
              setIsEditingCompany(true);
              // Reset visibility state when entering edit mode
              setShowTokenizationKey(false);
              setShowSecurityKey(false);
              setShowSendgridKey(false);
            }}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Company
            </Button>
          )}
          
          <AlertDialog open={isDeactivateDialogOpen} onOpenChange={setIsDeactivateDialogOpen}>
            <AlertDialogTrigger asChild>
              <Button variant={company?.status === 'active' ? 'destructive' : 'default'}>
                {company?.status === 'active' ? (
                  <>
                    <XCircle className="h-4 w-4 mr-2" />
                    Deactivate
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Activate
                  </>
                )}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {company?.status === 'active' ? 'Deactivate Tenant' : 'Activate Tenant'}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {company?.status === 'active' 
                    ? 'This will disable access for all users of this tenant. They will not be able to log in or use the system until reactivated.'
                    : 'This will enable access for all users of this tenant. They will be able to log in and use the system again.'}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleToggleStatus}
                  className={company?.status === 'active' ? 'bg-destructive hover:bg-destructive/90' : ''}
                >
                  {company?.status === 'active' ? 'Deactivate' : 'Activate'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" disabled={isImpersonationActive || isStartingImpersonation}>
                <LogIn className="h-4 w-4 mr-2" />
                Impersonate
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Impersonate Tenant</DialogTitle>
                <DialogDescription>
                  This will let you view the system as an admin of {company?.name || 'this tenant'}.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <p>Are you sure you want to impersonate this tenant?</p>
                <p className="text-sm text-muted-foreground mt-2">
                  This action will be logged for audit purposes. You can exit impersonation mode at any time using the banner at the top of the page.
                </p>
              </div>
              <DialogFooter>
                <Button
                  onClick={handleImpersonate}
                  disabled={isStartingImpersonation}
                >
                  {isStartingImpersonation ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Starting...
                    </>
                  ) : (
                    'Impersonate'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Subscription Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold capitalize">
              {company?.subscriptionPlan ?? 'Not specified'}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {company?.features?.recurringInvoices && "Recurring Invoices • "}
              {company?.features?.virtualTerminal && "Virtual Terminal • "}
              {company?.features?.userManagement && "User Management"}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <Users className="mr-2 h-4 w-4 text-muted-foreground" />
              <div className="text-2xl font-bold">
                {isLoadingUsers ? '...' : users?.length || 0}
              </div>
              <span className="ml-1 text-muted-foreground text-sm">
                / {company?.userLimit || 'Unlimited'}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {isLoadingUsers ? 'Loading...' : `${users?.filter(u => u.status === 'active').length || 0} active users`}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <BarChart4 className="mr-2 h-4 w-4 text-muted-foreground" />
              <div className="text-2xl font-bold">
                {isLoadingActivity ? '...' : activity?.length || 0}
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Last activity: {isLoadingActivity ? 'Loading...' : formatDate(activity?.[0]?.timestamp)}
            </p>
          </CardContent>
        </Card>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="features">Features</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
          <TabsTrigger
            value="danger"
            className="data-[state=active]:bg-destructive data-[state=active]:text-white border border-destructive text-destructive font-semibold"
          >
            Danger Zone
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  <CardTitle>Company Information</CardTitle>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setIsEditingCompany(!isEditingCompany)}
                >
                  {isEditingCompany ? 'Cancel' : 'Edit'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {isEditingCompany ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company-name">Company Name</Label>
                      <Input 
                        id="company-name" 
                        value={companyForm.name || ''}
                        onChange={(e) => setCompanyForm({...companyForm, name: e.target.value})}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="company-email">Email</Label>
                      <Input 
                        id="company-email" 
                        type="email"
                        value={companyForm.email || ''}
                        onChange={(e) => setCompanyForm({...companyForm, email: e.target.value})}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="company-phone">Phone</Label>
                      <Input 
                        id="company-phone" 
                        value={companyForm.phone || ''}
                        onChange={(e) => setCompanyForm({...companyForm, phone: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="company-notes">Notes (Internal)</Label>
                    <Textarea 
                      id="company-notes" 
                      value={companyForm.notes || ''}
                      onChange={(e) => setCompanyForm({...companyForm, notes: e.target.value})}
                      placeholder="Add internal notes about this tenant"
                      className="min-h-[100px]"
                    />
                  </div>
                  
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleUpdateCompany}
                      disabled={updateCompanyMutation.isPending}
                      className="gap-1"
                    >
                      {updateCompanyMutation.isPending && (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground">Company Name</h4>
                    <p>{company?.name || 'Not specified'}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground">Email</h4>
                    <p>{company?.email || 'Not specified'}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground">Phone</h4>
                    <p>{company?.phone || 'Not specified'}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground">Status</h4>
                    <div className="flex items-center gap-2">
                      {company?.status === 'active' ? (
                        <>
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          <span className="capitalize">Active</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 text-red-500" />
                          <span className="capitalize">Inactive</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {company?.notes && (
                    <div className="col-span-2">
                      <h4 className="text-sm font-medium text-muted-foreground">Notes</h4>
                      <p className="whitespace-pre-wrap">{company.notes}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                <CardTitle>Subscription Details</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Plan</h3>
                  <p className="capitalize">{company?.subscriptionPlan ?? 'Not specified'}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">User Limit</h3>
                  <p>{company?.userLimit || 'Unlimited'}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Status</h3>
                  <Badge variant={company?.status === 'active' ? 'success' : 'secondary'}>
                    {company?.status ?? 'Unknown'}
                  </Badge>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Created</h3>
                  <p>{company?.createdAt ? formatDate(company.createdAt) : 'Unknown'}</p>
                </div>
              </div>
              
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  <CardTitle>User Management</CardTitle>
                </div>
                <Button className="gap-1" disabled>
                  <PlusCircle className="h-4 w-4" />
                  <span>Add User</span>
                </Button>
              </div>
              <CardDescription>
                Manage users associated with this tenant
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoadingUsers ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-4">
                        <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : users && users.length > 0 ? (
                    users.map(user => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback>
                                {getInitials(user.displayName || user.email)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{user.displayName || 'No name'}</p>
                              <p className="text-sm text-muted-foreground">{user.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              user.status === 'active' ? 'success' :
                              user.status === 'pending' ? 'warning' : 'secondary'
                            }
                          >
                            {user.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(user.createdAt)}</TableCell>
                        <TableCell>{user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never'}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" className="gap-1" disabled>
                            <Edit className="h-4 w-4" />
                            <span className="sr-only">Edit</span>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-4">
                        No users found for this tenant
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="activity" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <BarChart4 className="h-5 w-5 text-primary" />
                <CardTitle>Activity Log</CardTitle>
              </div>
              <CardDescription>
                Recent actions performed for this tenant
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingActivity ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : activity && activity.length > 0 ? (
                <div className="space-y-4">
                  {activity.map(item => (
                    <div key={item.id} className="border-b pb-4 last:border-0">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{item.action}</p>
                          <p className="text-sm text-muted-foreground">{item.details}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">{formatDate(item.timestamp)}</p>
                          <p className="text-xs text-muted-foreground">By: {item.performerName}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No activity records found
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="features" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Settings2 className="h-5 w-5 text-primary" />
                <CardTitle>Enabled Features</CardTitle>
              </div>
              <CardDescription className="mt-2 text-muted-foreground">
                Manage which features are available for this tenant. Features can be enabled or disabled by the admin.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Recurring Invoices */}
                <div className="flex flex-col justify-between rounded-lg border p-4 bg-white shadow-sm hover:shadow-md transition-all">
                  <div className="flex items-center gap-2 mb-2">
                    <Label className="font-semibold">Recurring Invoices</Label>
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${company?.enableRecurringInvoices ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{company?.enableRecurringInvoices ? 'Enabled' : 'Disabled'}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">Allow creation of invoices that automatically rebill on a schedule.</p>
                  <Switch
                    id="enableRecurringInvoices"
                    checked={company?.enableRecurringInvoices ?? false}
                    onCheckedChange={(checked) => {
                      updateCompanyMutation.mutate({ 
                        enableRecurringInvoices: checked,
                        enableInstallmentInvoices: checked // Sync installment with recurring
                      });
                    }}
                    className="data-[state=checked]:bg-primary"
                    disabled={false}
                  />
                </div>
                {/* Installment Invoices */}
                <div className="flex flex-col justify-between rounded-lg border p-4 bg-white shadow-sm hover:shadow-md transition-all">
                  <div className="flex items-center gap-2 mb-2">
                    <Label className="font-semibold">Installment Invoices</Label>
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${company?.enableRecurringInvoices ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{company?.enableRecurringInvoices ? 'Enabled' : 'Disabled'}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">Allow splitting invoice payments into multiple installments. (Synced with Recurring Invoices)</p>
                  <Switch
                    id="enableInstallmentInvoices"
                    checked={company?.enableRecurringInvoices ?? false} // Always synced with recurring
                    onCheckedChange={() => {
                      // Do nothing - this is controlled by recurring invoices
                    }}
                    className="data-[state=checked]:bg-primary"
                    disabled={true} // Always disabled since it follows recurring
                  />
                </div>
                {/* Virtual Terminal */}
                <div className="flex flex-col justify-between rounded-lg border p-4 bg-white shadow-sm hover:shadow-md transition-all">
                  <div className="flex items-center gap-2 mb-2">
                    <Label className="font-semibold">Virtual Terminal</Label>
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${company?.features?.virtualTerminal ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{company?.features?.virtualTerminal ? 'Enabled' : 'Disabled'}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">Process payments directly from the dashboard in a PCI-compliant way.</p>
                  <Switch
                    checked={company?.features?.virtualTerminal ?? false}
                    disabled={false}
                    onCheckedChange={(checked) => {
                      updateCompanyMutation.mutate({ 
                        features: { 
                          ...company.features, 
                          virtualTerminal: checked 
                        } 
                      });
                    }}
                    className="data-[state=checked]:bg-primary"
                  />
                </div>
                {/* User Management */}
                <div className="flex flex-col justify-between rounded-lg border p-4 bg-white shadow-sm hover:shadow-md transition-all">
                  <div className="flex items-center gap-2 mb-2">
                    <Label className="font-semibold">User Management</Label>
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${company?.features?.userManagement ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{company?.features?.userManagement ? 'Enabled' : 'Disabled'}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">Manage users and permissions for this tenant.</p>
                  <Switch
                    checked={company?.features?.userManagement ?? false}
                    disabled={false}
                    onCheckedChange={(checked) => {
                      updateCompanyMutation.mutate({ 
                        features: { 
                          ...company.features, 
                          userManagement: checked 
                        } 
                      });
                    }}
                    className="data-[state=checked]:bg-primary"
                  />
                </div>
                {/* Donations */}
                <div className="flex flex-col justify-between rounded-lg border p-4 bg-white shadow-sm hover:shadow-md transition-all">
                  <div className="flex items-center gap-2 mb-2">
                    <Label className="font-semibold">Donations</Label>
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${company?.features?.donations ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{company?.features?.donations ? 'Enabled' : 'Disabled'}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">Accept donations as part of your payment flows.</p>
                  <Switch
                    checked={company?.features?.donations ?? false}
                    disabled={false}
                    onCheckedChange={(checked) => {
                      updateCompanyMutation.mutate({ 
                        features: { 
                          ...company.features, 
                          donations: checked 
                        } 
                      });
                    }}
                    className="data-[state=checked]:bg-primary"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="payments" className="space-y-4">
          {id && (
            <PaymentGatewaysForm companyId={id} />
          )}
        </TabsContent>
        
        <TabsContent value="danger" className="space-y-4">
          <Card className="border-destructive">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-destructive" />
                <CardTitle className="text-destructive">Danger Zone</CardTitle>
              </div>
              <CardDescription className="mt-2">
                This action is <strong>irreversible</strong>. Deleting this tenant will permanently remove all associated data, users, invoices, and settings. Proceed with caution.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Delete Tenant Section */}
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 pb-6 border-b">
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold mb-2">Delete Entire Tenant</h3>
                    <div className="text-sm text-muted-foreground">
                      <p className="mb-2">This will permanently delete:</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>All users related to this tenant</li>
                        <li>All invoices, transactions, and donations</li>
                        <li>All customer data and payment methods</li>
                        <li>All products and company settings</li>
                        <li>All recurring invoice templates</li>
                      </ul>
                    </div>
                  </div>
                  <Button
                    variant="destructive"
                    size="lg"
                    className="mt-2 md:mt-0 md:min-w-[180px]"
                    onClick={() => setIsDeleteDialogOpen(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Tenant
                  </Button>
                </div>

                {/* Delete Data Only Section */}
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold mb-2">Delete Business Data Only</h3>
                    <div className="text-sm text-muted-foreground">
                      <p className="mb-2">This will permanently delete:</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>All transactions and invoices</li>
                        <li>All donations and saved payment methods</li>
                        <li>All products and customers</li>
                      </ul>
                      <p className="mt-2 text-green-700 font-medium">Company settings and users will remain intact.</p>
                    </div>
                  </div>
                  <Button
                    variant="destructive"
                    size="lg"
                    className="mt-2 md:mt-0 md:min-w-[180px]"
                    onClick={() => setIsDeleteTransactionsDialogOpen(true)}
                    disabled={isDeletingTransactions}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Data Only
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          <ConfirmDialog open={isDeleteTransactionsDialogOpen} onOpenChange={setIsDeleteTransactionsDialogOpen}>
            <ConfirmDialogContent className="max-w-md">
              <ConfirmDialogHeader>
                <ConfirmDialogTitle className="text-destructive">Delete Business Data Only</ConfirmDialogTitle>
              </ConfirmDialogHeader>
              <div className="py-4 text-destructive">
                <p>This will <strong>permanently delete</strong> all transactions, invoices, donations, saved payment methods, products, and customers for this tenant. This cannot be undone. Company settings and users will remain intact. Are you sure?</p>
              </div>
              <ConfirmDialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setIsDeleteTransactionsDialogOpen(false)} disabled={isDeletingTransactions}>No</Button>
                <Button variant="destructive" onClick={handleDeleteTransactionsOnly} disabled={isDeletingTransactions}>
                  {isDeletingTransactions ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Yes, Delete Data
                </Button>
              </ConfirmDialogFooter>
            </ConfirmDialogContent>
          </ConfirmDialog>
        </TabsContent>
      </Tabs>
      
      {/* Delete Tenant Dialog */}
      <DeleteTenantDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        tenantName={company?.name || ''}
        tenantId={id || ''}
        onConfirm={handleDeleteTenant}
      />
      
    </div>
  );
};

export default TenantDetail; 
